import { signalStore, withComputed, withMethods, withProps, withState } from '@ngrx/signals';
import { updateState, withDevtools } from '@angular-architects/ngrx-toolkit';
import { ICompany } from '@models/company';
import {
  setLoaded,
  setLoading,
  setLoadingError,
  setUpdated,
  setUpdating,
  setUpdatingError,
  withCallStatus,
} from '@ngrx-helpers/call-status';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { exhaustMap, pipe, switchMap, tap } from 'rxjs';
import { computed, inject } from '@angular/core';
import { CompanyApiService } from '../api/company-api.service';
import { tapResponse } from '@ngrx/operators';
import { loadOperationAsync } from '@ngrx-helpers/call-status-operations';

type State = {
  company: ICompany;
  passwordChanged: boolean;
};

const initialState: State = {
  company: {
    createdAt: '',
    customer: '',
    email: '',
    id: '',
    license_limit: 0,
    name: '',
    next_purchase: null,
    purchased: null,
    subscription: null,
    subscription_status: null,
    total_amount: null,
    updatedAt: '',
    temporary_pass: null,
    temporary_pass_expired_at: null,
    temporary_salt: null,
  },
  passwordChanged: false,
};

export const CompanyStore = signalStore(
  { providedIn: 'root' },
  withDevtools('company'),

  withState<State>(initialState),
  withCallStatus(),

  withProps(() => ({
    api: inject(CompanyApiService),
  })),

  withComputed(state => ({
    isLoaded: computed(() => state.company().id !== ''),
  })),

  withMethods(state => ({
    loadCompany: rxMethod<void>(
      pipe(
        tap(() => updateState(state, 'load company', setLoading())),
        exhaustMap(() =>
          state.api.loadCompany().pipe(
            tapResponse(
              company => updateState(state, 'company loaded', setLoaded(), () => ({ company })),
              (error: Error) => updateState(state, 'company load error', setLoadingError(error))
            )
          )
        )
      )
    ),
    changeName: rxMethod<string>(
      pipe(
        tap(() => updateState(state, 'change name', setUpdating())),
        switchMap(name =>
          state.api.changeName(name).pipe(
            tapResponse(
              () =>
                updateState(state, 'name changed', setUpdated(), state => ({
                  company: { ...state.company, name },
                })),
              (error: Error) => updateState(state, 'name change error', setUpdatingError(error))
            )
          )
        )
      )
    ),

    setIsPasswordChanged: rxMethod<boolean>(
      pipe(
        tap(isChanged =>
          updateState(state, `set password changed to ${isChanged}`, setUpdated(), () => ({
            passwordChanged: isChanged,
          }))
        )
      )
    ),
  })),

  withMethods(state => ({
    async waitForLoadingCompany(force = false) {
      return await loadOperationAsync({
        state,
        load: s => s.loadCompany(),
        result: s => s.company(),
        exists: s => s.company().id !== '',
        force,
      });
    },
  }))
);
